import type { ReactElement } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlatformBadge } from '@/components/shared/platform/PlatformBadge';
import type { AgentCommandEvent } from '@kirin/api-client/contracts/agent-command-events';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { agentPlatformId, NO_BRANCH_TOOLTIP } from './constants';

/** Agent cell: platform icon + product name chip (the IDE/Platform look). */
export function AgentBadge({ agentName }: { agentName: string }): ReactElement {
  return (
    <PlatformBadge platform={agentPlatformId(agentName)} showTooltip={false} />
  );
}

/**
 * Multi-chip agent cell for the aggregated Packages view (KIR-3807): one
 * platform chip per distinct agent in the group (the alerts IDE/Platform look).
 */
export function AgentsCell({ agents }: { agents: string[] }): ReactElement {
  if (agents.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {agents.map(agent => (
        <AgentBadge key={agent} agentName={agent} />
      ))}
    </div>
  );
}

function basename(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : path;
}

/**
 * Project cell: the resolved projects-inventory name as a link to the
 * single-project page (org view only). Falls back to the folder basename when
 * the event isn't linked to a project (personal view, non-git folders). The
 * link stops row-click propagation so it doesn't also open the detail dialog.
 */
export function ProjectCell({
  row,
  organizationId,
}: {
  // Structural: both a flat command event and an aggregated package row carry
  // the resolved project fields.
  row: Pick<AgentCommandEvent, 'projectName' | 'projectSlug' | 'projectPath'>;
  organizationId: string | undefined;
}): ReactElement {
  const label =
    row.projectName ?? (row.projectPath ? basename(row.projectPath) : '—');
  if (row.projectSlug && organizationId) {
    return (
      <Link
        to={`/${organizationId}/inventory/projects/${encodeURIComponent(row.projectSlug)}`}
        className="block truncate text-primary hover:underline"
        onClick={e => e.stopPropagation()}
      >
        {label}
      </Link>
    );
  }
  return <span className="block truncate">{label}</span>;
}

/**
 * A count rendered as an inline button that opens a drill-down dialog
 * (Occurrences / Endpoints on the aggregated views). A zero count renders as
 * plain muted text (nothing to drill into). Styling matches the project-wide
 * inventory count links (`ClickableUserCount` / inventory `EndpointsCell`):
 * solid primary underline, `decoration-primary/40`, so every clickable count
 * reads the same across the product (KIR-3833).
 */
export function CountButton({
  count,
  label,
  onClick,
}: {
  count: number;
  label: string;
  onClick: () => void;
}): ReactElement {
  if (count <= 0) return <span className="text-muted-foreground">0</span>;
  return (
    <button
      type="button"
      aria-label={label}
      className="font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
    >
      {count}
    </button>
  );
}

/** Agent filter-dropdown option: selection check + platform chip. */
export function AgentFilterOption({
  value,
  isSelected,
}: {
  value: string;
  isSelected: boolean;
}): ReactElement {
  return (
    <div className="flex items-center gap-2">
      {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
      <PlatformBadge platform={agentPlatformId(value)} showTooltip={false} />
    </div>
  );
}

/** Branch cell; an empty one explains why on hover (KIR-4848). */
export function BranchCell({
  branch,
}: {
  branch: string | null;
}): ReactElement {
  if (branch && branch.trim()) {
    return <span className="block truncate">{branch}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help" aria-label={NO_BRANCH_TOOLTIP}>
          —
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{NO_BRANCH_TOOLTIP}</TooltipContent>
    </Tooltip>
  );
}
